export class SmoothCaret {
    constructor(containerId) {
        this.caretElement = null;
        this.targetElement = null;
        this.animationFrame = null;
        this.isVisible = false;
        this.container = null;
        this.currentX = 0;
        this.currentY = 0;
        this.targetX = 0;
        this.targetY = 0;
        this.currentHeight = 0;
        this.targetHeight = 0;
        this.smoothingFactor = 0.2; // Lower = smoother but slower
        this.positionAfter = false; // Whether to position cursor after the element
        this.containerId = containerId;
        const container = document.getElementById(containerId);
        if (!container) {
            console.error(`Container with id "${containerId}" not found, will retry`);
            // Retry after a short delay
            setTimeout(() => {
                const retryContainer = document.getElementById(containerId);
                if (retryContainer) {
                    this.initialize(retryContainer);
                }
                else {
                    console.error(`Container with id "${containerId}" still not found after retry`);
                }
            }, 100);
            return;
        }
        this.initialize(container);
    }
    initialize(container) {
        this.container = container;
        // Create caret element - animated blinking cursor
        this.caretElement = document.createElement('div');
        this.caretElement.className = 'smooth-caret';
        this.caretElement.setAttribute('aria-hidden', 'true');
        // Set inline styles to ensure visibility - set everything directly
        this.caretElement.style.position = 'absolute';
        this.caretElement.style.width = '2px';
        this.caretElement.style.backgroundColor = '#fbbf24';
        this.caretElement.style.pointerEvents = 'none';
        this.caretElement.style.zIndex = '9999';
        this.caretElement.style.display = 'none';
        this.caretElement.style.opacity = '1';
        this.caretElement.style.visibility = 'hidden';
        this.caretElement.style.top = '0';
        this.caretElement.style.left = '0';
        // Add animation directly via inline style as fallback
        this.caretElement.style.animation = 'caret-blink 1.06s step-end infinite';
        container.style.position = 'relative'; // Ensure container is positioned
        container.appendChild(this.caretElement);
        // Debug: log that caret was created
        console.log('Caret element created:', this.caretElement);
        // Listen for position updates
        document.addEventListener('caret-position-update', (e) => {
            this.targetElement = e.detail.element;
            this.positionAfter = e.detail.positionAfter || false;
            this.updateTargetPosition();
        });
        this.startAnimation();
    }
    startAnimation() {
        const animate = () => {
            // Always update position if we have a target element, even when not visible
            // This ensures smooth transitions when visibility changes
            if (this.targetElement) {
                this.smoothUpdatePosition();
            }
            this.animationFrame = requestAnimationFrame(animate);
        };
        animate();
    }
    updateTargetPosition() {
        if (!this.targetElement || !this.container) {
            return;
        }
        const rect = this.targetElement.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        // Calculate target position relative to container's visible area
        // getBoundingClientRect() gives viewport-relative positions, so we subtract
        // the container's viewport position to get position relative to container's visible area
        // The caret is absolutely positioned, so it should be relative to the container's visible area
        this.targetX = rect.left - containerRect.left + (this.positionAfter ? rect.width : 0);
        this.targetY = rect.top - containerRect.top;
        this.targetHeight = rect.height;
        // If this is the first position or we're very far, set immediately
        if ((this.currentX === 0 && this.currentY === 0) ||
            Math.abs(this.targetX - this.currentX) > 100 ||
            Math.abs(this.targetY - this.currentY) > 50) {
            this.currentX = this.targetX;
            this.currentY = this.targetY;
            this.currentHeight = this.targetHeight;
            this.applyPosition();
        }
    }
    smoothUpdatePosition() {
        if (!this.targetElement || !this.caretElement || !this.container) {
            return;
        }
        // Recalculate target position on each frame to account for scroll changes
        // This ensures the caret follows correctly when the container scrolls
        const rect = this.targetElement.getBoundingClientRect();
        const containerRect = this.container.getBoundingClientRect();
        // Calculate position relative to container's visible area (not scrollable content)
        // This ensures the caret stays at the element's current visible position
        const newTargetX = rect.left - containerRect.left + (this.positionAfter ? rect.width : 0);
        const newTargetY = rect.top - containerRect.top;
        const newTargetHeight = rect.height;
        // Update target position - this will change as the container scrolls
        this.targetX = newTargetX;
        this.targetY = newTargetY;
        this.targetHeight = newTargetHeight;
        // Smooth interpolation using linear interpolation
        const dx = this.targetX - this.currentX;
        const dy = this.targetY - this.currentY;
        const dh = this.targetHeight - this.currentHeight;
        // If close enough, snap to target
        if (Math.abs(dx) < 0.5 && Math.abs(dy) < 0.5 && Math.abs(dh) < 0.5) {
            this.currentX = this.targetX;
            this.currentY = this.targetY;
            this.currentHeight = this.targetHeight;
        }
        else {
            // Smooth interpolation
            this.currentX += dx * this.smoothingFactor;
            this.currentY += dy * this.smoothingFactor;
            this.currentHeight += dh * this.smoothingFactor;
        }
        this.applyPosition();
    }
    applyPosition() {
        if (!this.caretElement)
            return;
        // Use transform for better performance (GPU accelerated)
        this.caretElement.style.transform = `translate(${this.currentX}px, ${this.currentY}px)`;
        this.caretElement.style.height = `${this.currentHeight || 24}px`; // Default height if not set
        // Ensure opacity is set for blinking animation
        if (this.isVisible) {
            this.caretElement.style.opacity = '1';
        }
    }
    setVisible(visible) {
        this.isVisible = visible;
        // Ensure caret element exists
        if (!this.caretElement) {
            const container = this.container || document.getElementById(this.containerId);
            if (container) {
                this.initialize(container);
            }
            else {
                console.error('Cannot set caret visible: container not found');
                return;
            }
        }
        // Re-attach if soft-error UI or renderWords wiped the container
        if (this.caretElement &&
            this.container &&
            !this.container.contains(this.caretElement)) {
            this.container.appendChild(this.caretElement);
        }
        if (visible) {
            // If no target element yet, try to find the first letter
            if (!this.targetElement) {
                this.findInitialPosition();
            }
            // Set all styles directly to ensure visibility
            this.caretElement.style.display = 'block';
            this.caretElement.style.visibility = 'visible';
            this.caretElement.style.opacity = '1';
            this.caretElement.classList.add('caret-visible');
            // Force immediate position update when made visible
            if (this.targetElement) {
                this.updateTargetPosition();
            }
            else {
                // If still no target, use fallback position
                this.findInitialPosition();
            }
            // Apply position immediately
            this.applyPosition();
        }
        else {
            this.caretElement.style.display = 'none';
            this.caretElement.style.visibility = 'hidden';
            this.caretElement.classList.remove('caret-visible');
        }
    }
    findInitialPosition() {
        if (!this.container)
            return;
        // Try to find the first letter element in the container
        const firstLetter = this.container.querySelector('[data-letter-id]');
        if (firstLetter) {
            this.targetElement = firstLetter;
            this.positionAfter = false;
            // Use setTimeout to ensure DOM is ready
            setTimeout(() => {
                this.updateTargetPosition();
                this.applyPosition();
            }, 0);
        }
        else {
            // Fallback: try to find any text element or position at start
            const firstTextElement = this.container.querySelector('span, div');
            if (firstTextElement) {
                const rect = firstTextElement.getBoundingClientRect();
                const containerRect = this.container.getBoundingClientRect();
                this.currentX = rect.left - containerRect.left;
                this.currentY = rect.top - containerRect.top;
                this.currentHeight = rect.height || 24;
            }
            else {
                // Ultimate fallback: position at top-left of container with default height
                this.currentX = 0;
                this.currentY = 0;
                this.currentHeight = 24; // Default line height
            }
            this.applyPosition();
        }
    }
    destroy() {
        if (this.animationFrame) {
            cancelAnimationFrame(this.animationFrame);
            this.animationFrame = null;
        }
        this.caretElement?.remove();
        this.caretElement = null;
    }
}
